import { Icon, IconSizeClasses } from '@eppendorf/vnls-react-components';
import {
  StabilityAssessment,
  StabilityAssessmentStatus,
} from '@eppendorf/vnls-reporting-service-utils';
import { useTranslation } from 'react-i18next';

import { Card } from '$components/card';
import { DonutChart, type DonutChartRange } from '$components/donut-chart/donut-chart';
import { getIconNameForParameterName } from '$features/monitoring/monitoring.utils';
import { formatDurationFromSeconds } from '$features/report-hub/report-hub.utils';
import { SetpointChangesDropdown } from '$features/report-hub/stability-report/setpoint-changes-dropdown';
import { ReportParameter } from '$features/report-hub/stability-report/stability-report.types';

const ASSESSMENT_COLORS: Record<StabilityAssessmentStatus, string> = {
  good: '#168400',
  alert: '#db171e',
  warning: '#e86d00',
  offline: '#4a505a',
  'no-data': '#4a505a',
  'alerts-disabled': '#4a505a',
  'thresholds-unavailable': '#4a505a',
  'history-unavailable': '#4a505a',
};

function TimeInRangesSection({ assessment }: { assessment: StabilityAssessment[] }) {
  const { t } = useTranslation();

  const ranges: DonutChartRange[] = assessment.map((range) => ({
    label: t(`stabilityDetail.assessment.${range.status}`),
    percentage: range.percentage,
    color: ASSESSMENT_COLORS[range.status],
    detail: formatDurationFromSeconds(range.duration),
  }));

  return (
    <div className="flex flex__dir--column">
      <div className="flex flex__ai--center gap-xs m-bottom-m">
        <Icon name="time" size={IconSizeClasses.XSmall} className="bg-gray-800" />
        <span className="font-weight-semibold">
          {t('stabilityDetail.timeInRanges.title')}
        </span>
      </div>
      <div className="flex flex__wrap--wrap gap-m flex__ai--center">
        <DonutChart ranges={ranges} />
        <div className="font-size-s flex flex__dir--column gap-s">
          {assessment.map((range) => (
            <div key={range.status} className="flex gap-xs flex__ai--start">
              <span
                className="stability-sidecar__legend-dot"
                style={{
                  backgroundColor: ASSESSMENT_COLORS[range.status],
                  flexShrink: 0,
                  marginTop: 4,
                }}
              />
              <div>
                <div>{t(`stabilityDetail.assessment.${range.status}`)}</div>
                <span className="font-weight-medium">
                  {range.percentage % 1 === 0
                    ? `${range.percentage}%`
                    : `${range.percentage.toFixed(1)}%`}
                  {range.duration !== '0' &&
                    ` · ${formatDurationFromSeconds(range.duration)}`}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const LEGACY_ASSESSMENT_STATUSES = new Map<string, StabilityAssessmentStatus>([
  ['Good', 'good'],
  ['Alert', 'alert'],
  ['Warning', 'warning'],
]);

interface ParameterSectionProps {
  parameter: ReportParameter;
}

export function ParameterSection({ parameter }: ParameterSectionProps) {
  const { t } = useTranslation();
  const hasSetpoints = parameter.setpoints && parameter.setpoints.length > 0;
  const assessment: StabilityAssessment[] = parameter.assessment?.length
    ? parameter.assessment
    : (parameter.timeInRanges ?? []).map((range) => ({
        percentage: range.percentage,
        duration: range.duration,
        status: LEGACY_ASSESSMENT_STATUSES.get(range.label) ?? 'history-unavailable',
      }));

  return (
    <Card className="flex m-bottom-l p-xl stability-sidecar__parameter-card">
      <div className="stability-sidecar__card-left">
        <div className="flex flex__ai--center gap-s m-bottom-m">
          <Icon
            name={getIconNameForParameterName(parameter.name)}
            size={IconSizeClasses.XSmall}
            className="bg-gray-900"
          />
          <span className="font-weight-semibold">{parameter.name}</span>
        </div>
        {parameter.setpoints !== undefined && parameter.setpoints.length > 1 && (
          <SetpointChangesDropdown setpoints={parameter.setpoints.slice(1)} />
        )}
        <div className="stability-sidecar__stats-grid">
          {hasSetpoints && (
            <div className="flex flex__dir--column gap-xs">
              <span className="color-gray-800 font-size-s">
                {t('stabilityDetail.parameterStats.setpoint')}
              </span>
              <span className="font-weight-semibold">
                {parameter.setpoints![parameter.setpoints!.length - 1]?.value}
              </span>
            </div>
          )}
          {parameter.average !== undefined && (
            <div className="flex flex__dir--column gap-xs">
              <span className="color-gray-800 font-size-s">
                {t('stabilityDetail.parameterStats.average')}
              </span>
              <span className="font-weight-semibold">
                {parameter.average ?? t('stabilityDetail.assessment.no-data')}
              </span>
            </div>
          )}
          {parameter.minimum !== undefined && (
            <div className="flex flex__dir--column gap-xs">
              <span className="color-blue-500 font-size-s">
                {t('stabilityDetail.parameterStats.minimum')}
              </span>
              <span className="font-weight-semibold color-blue-600">
                {parameter.minimum ?? t('stabilityDetail.assessment.no-data')}
              </span>
            </div>
          )}
          {parameter.maximum !== undefined && (
            <div className="flex flex__dir--column gap-xs">
              <span className="color-purple-500 font-size-s">
                {t('stabilityDetail.parameterStats.maximum')}
              </span>
              <span className="font-weight-semibold color-purple-600">
                {parameter.maximum ?? t('stabilityDetail.assessment.no-data')}
              </span>
            </div>
          )}
        </div>
      </div>
      {assessment.length > 0 && (
        <div className="stability-sidecar__card-right">
          <TimeInRangesSection assessment={assessment} />
        </div>
      )}
    </Card>
  );
}
